import { useFormikContext } from 'formik';
import { ReactElement } from 'react';
import { object, reach } from 'yup';
import { NeglectedOrDelinquentInformationFormValues } from '../../../types/NeglectedOrDelinquentInformationModel';
import { GED_SECTIONS } from '../../../types/NeglectedOrDelinquentGEDFields';
import { PERIOD_COUNT_FIELDS } from '../../../types/NeglectedOrDelinquentPeriodCountFields';
import { PanelType } from '../../../types/PanelType';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import { isValidNeglectedOrDelinquentCount } from '../../../utilities/neglectedOrDelinquentUtilities';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

const NeglectedOrDelinquentGEDPanel = ({
    disabled,
    validationSchema,
}: {
    disabled: boolean;
    validationSchema: ReturnType<
        typeof createNeglectedOrDelinquentInformationValidationSchema
    >;
}): ReactElement => {
    const { values, errors } =
        useFormikContext<NeglectedOrDelinquentInformationFormValues>();

    return (
        <Panel panelType={PanelType.INFO} heading="GED (High School Equivalency)">
            <p>
                Provide information about GED attainment and enrollment during or
                after participation in the program.
            </p>
            {GED_SECTIONS.map(({ key, description, totalLabel, totalField }) => {
                const group = values.ged[key];
                const canCalculate = PERIOD_COUNT_FIELDS.every(({ key: fieldKey }) =>
                    isValidNeglectedOrDelinquentCount(group[fieldKey]));
                const total = canCalculate
                    ? PERIOD_COUNT_FIELDS.reduce(
                        (sum, { key: fieldKey }) => sum + Number(group[fieldKey]), 0
                    ) : '';
                const warning = errors[totalField];
                return (
                    <section key={key}>
                        <p>
                            Provide the <strong>number of students</strong> {description}
                        </p>
                        <div className="nord-ged">
                            {PERIOD_COUNT_FIELDS.map(({ key: fieldKey, label }) => {
                                const field = `ged.${key}.${fieldKey}`;
                                const inputSchema = object().shape({
                                    [field]: reach(validationSchema, field),
                                });
                                return (
                                    <FormTextbox
                                        key={fieldKey}
                                        field={field}
                                        displayName={label}
                                        disabled={disabled}
                                        validationSchema={inputSchema}
                                    />
                                );
                            })}
                            <div className="field-container disabled">
                                <FormInputLabel
                                    field={totalField}
                                    displayName={totalLabel}
                                    disabled
                                />
                                <input
                                    id={totalField}
                                    className="data-input"
                                    type="text"
                                    disabled
                                    readOnly
                                    value={total}
                                />
                            </div>
                        </div>
                        {typeof warning === 'string' && (
                            <p className="nord-demographics-error" role="alert">
                                {warning}
                            </p>
                        )}
                    </section>
                );
            })}
        </Panel>
    );
};

export default NeglectedOrDelinquentGEDPanel;
